import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import './MenuButton.css';

/**
 * MenuButton
 *
 * A button that shows a dropdown menu on click. Menu items are rendered
 * using the same `Button` component to preserve visual consistency.
 */
function MenuButton({
  children,
  icon: TriggerIcon,
  variant = 'primary',
  size = 'default',
  type = 'default',
  htmlType = 'button',

  // Menu behavior
  items = [], // [{ key, label, icon, onClick, disabled, variant, type }]
  onItemSelect, // (item) => void
  align = 'left', // 'left' | 'right'
  closeOnSelect = true,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  usePortal = true,

  // Responsive props forwarded to trigger button
  collapseOnSmallScreen = false,
  lowerThreshold = 500,
  upperThreshold = 500,

  // Icon props forwarded to trigger button
  iconSize = 'default',
  isSpinning = false,

  // Special states forwarded to trigger button
  highlight = false,
  isRecording = false,

  ...props
}) {
  const isControlled = typeof controlledOpen === 'boolean';
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, right: 'auto', width: 0 });

  const triggerButtonProps = useMemo(
    () => ({
      variant,
      size,
      type,
      htmlType,
      collapseOnSmallScreen,
      lowerThreshold,
      upperThreshold,
      iconSize,
      isSpinning,
      highlight,
      isRecording,
    }),
    [
      variant,
      size,
      type,
      htmlType,
      collapseOnSmallScreen,
      lowerThreshold,
      upperThreshold,
      iconSize,
      isSpinning,
      highlight,
      isRecording,
    ]
  );

  function setOpen(next) {
    if (!isControlled) setUncontrolledOpen(next);
    if (onOpenChange) onOpenChange(next);
  }

  function toggleOpen() {
    setOpen(!open);
  }

  function handleSelect(item, event) {
    if (item && typeof item.onClick === 'function') {
      item.onClick(event);
    }
    if (onItemSelect) onItemSelect(item, event);
    if (closeOnSelect) setOpen(false);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    function handleDocumentMouseDown(event) {
      const rootEl = rootRef.current;
      const menuEl = menuRef.current;
      if (!rootEl) return;
      // If click happens inside trigger or inside dropdown, do nothing
      if (rootEl.contains(event.target)) return;
      if (menuEl && menuEl.contains(event.target)) return;
      setOpen(false);
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [open]);

  // Close on Escape, basic keyboard nav focus management
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
      }
      if ((event.key === 'ArrowDown' || event.key === 'Down') && menuRef.current) {
        const firstButton = menuRef.current.querySelector('button');
        if (firstButton) firstButton.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Compute portal position relative to trigger
  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const triggerEl = triggerRef.current;
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();
      const top = Math.round(rect.bottom + 6);
      if (align === 'right') {
        const right = Math.round(window.innerWidth - rect.right);
        setMenuPosition({ top, left: 'auto', right, width: rect.width });
      } else {
        const left = Math.round(rect.left);
        setMenuPosition({ top, left, right: 'auto', width: rect.width });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, align]);

  const localDropdownStyle = useMemo(() => {
    return { left: align === 'left' ? 0 : 'auto', right: align === 'right' ? 0 : 'auto' };
  }, [align]);

  return (
    <div className="menu-button" ref={rootRef}>
      <Button
        {...triggerButtonProps}
        icon={TriggerIcon}
        onClick={toggleOpen}
        ref={triggerRef}
        {...props}
      >
        {children}
      </Button>

      {open && (
        usePortal
          ? createPortal(
              <div
                className="menu-button-dropdown menu-button-portal"
                style={{
                  top: menuPosition.top,
                  left: menuPosition.left,
                  right: menuPosition.right,
                  position: 'fixed',
                  minWidth: Math.max(180, menuPosition.width),
                  background: '#fff',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 8,
                  padding: 6,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  color: '#111',
                  pointerEvents: 'auto'
                }}
                ref={menuRef}
              >
                {items.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <Button
                      key={item.key || String(item.label)}
                      icon={ItemIcon}
                      variant={item.variant || variant}
                      size={item.size || size}
                      type={item.type || 'default'}
                      disabled={item.disabled}
                      onClick={(e) => handleSelect(item, e)}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </div>,
              document.body
            )
          : (
              <div className="menu-button-dropdown" style={localDropdownStyle} ref={menuRef}>
                {items.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <Button
                      key={item.key || String(item.label)}
                      icon={ItemIcon}
                      variant={item.variant || variant}
                      size={item.size || size}
                      type={item.type || 'default'}

                      disabled={item.disabled}
                      onClick={(e) => handleSelect(item, e)}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </div>
            )
      )}
    </div>
  );
}

export default MenuButton;


